/*
 * SignReceiptDialog — reusable dialog that turns a "read + acknowledge"
 * flow across the Central de Comunicação (Código de Ética, Banners,
 * Vídeos) into a formal signature with OTP (SMS or E-mail) + signed
 * PDF receipt.
 *
 * Usage:
 *   <SignReceiptDialog
 *     open={open} onOpenChange={setOpen}
 *     sourceKind="codigo_etica"  // 'banner' | 'video' | 'codigo_etica'
 *     sourceId={item.id}
 *     documentTitle={item.title}
 *     onSigned={(signatureId) => { ... }} />
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  ShieldCheck, Smartphone, Mail, Loader2, CheckCircle2, ArrowLeft,
} from "lucide-react";

const STEP_PREPARE = "prepare";
const STEP_CHANNEL = "channel";
const STEP_OTP = "otp";
const STEP_DONE = "done";

export default function SignReceiptDialog({
  open, onOpenChange, sourceKind, sourceId, documentTitle,
  onSigned,
}) {
  const { token } = useAuth();
  const [step, setStep] = useState(STEP_PREPARE);
  const [receiptId, setReceiptId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [channel, setChannel] = useState(null);   // 'sms' | 'email'
  const [toMasked, setToMasked] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [sending, setSending] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signatureId, setSignatureId] = useState(null);

  // Reset on close.
  useEffect(() => {
    if (open) return;
    setStep(STEP_PREPARE);
    setReceiptId(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setChannel(null);
    setToMasked("");
    setOtpCode("");
    setSignatureId(null);
  }, [open]);

  // Prepare the receipt on open — creates it (or reuses existing) and
  // loads a preview blob.
  const prepare = useCallback(async () => {
    if (!token || !sourceKind || !sourceId) return;
    try {
      const r = await axios.post(
        `${API}/employee/document-receipts/prepare`,
        { token, source_kind: sourceKind, source_id: sourceId },
        { timeout: 30000 },
      );
      const rid = r.data?.id;
      if (!rid) throw new Error("Recibo indisponível");
      // If already signed, jump straight to done + download.
      if (r.data?.status === "signed") {
        setSignatureId(r.data.signature_id || null);
        setReceiptId(rid);
        setStep(STEP_DONE);
        return;
      }
      setReceiptId(rid);
      // load preview
      const pv = await axios.get(
        `${API}/employee/document-receipts/${rid}/preview`,
        { params: { token }, responseType: "blob", timeout: 30000 },
      );
      const url = URL.createObjectURL(pv.data);
      setPreviewUrl(url);
      setStep(STEP_CHANNEL);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao preparar o recibo");
      onOpenChange(false);
    }
  }, [token, sourceKind, sourceId, onOpenChange]);

  useEffect(() => {
    if (open && step === STEP_PREPARE) prepare();
  }, [open, step, prepare]);

  const sendOtp = async (ch) => {
    if (!receiptId) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/employee/document-receipts/${receiptId}/send-otp`,
        { token, channel: ch },
        { timeout: 20000 },
      );
      setChannel(ch);
      setToMasked(r.data?.to_masked || "");
      setStep(STEP_OTP);
      toast.success(
        ch === "sms"
          ? `Código enviado por SMS para ${r.data?.to_masked || ""}`
          : `Código enviado por e-mail para ${r.data?.to_masked || ""}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar o código");
    } finally {
      setSending(false);
    }
  };

  const confirmOtp = async () => {
    const code = (otpCode || "").trim();
    if (!code) {
      toast.error("Informe o código recebido");
      return;
    }
    setSigning(true);
    try {
      const r = await axios.post(
        `${API}/employee/document-receipts/${receiptId}/confirm-otp`,
        { token, channel, code },
        { timeout: 30000 },
      );
      if (!r.data?.signed) throw new Error("Recibo não assinado");
      setSignatureId(r.data.signature_id);
      setStep(STEP_DONE);
      onSigned?.(r.data.signature_id);
      toast.success("Recibo assinado com sucesso!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido ou expirado");
    } finally {
      setSigning(false);
    }
  };

  const downloadSigned = () => {
    if (!receiptId) return;
    const url = `${API}/employee/document-receipts/${receiptId}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="sign-receipt-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Termo de Ciência e Aceite
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {documentTitle}
          </DialogDescription>
        </DialogHeader>

        {step === STEP_PREPARE && (
          <div className="flex flex-col items-center justify-center py-10 gap-3"
               data-testid="sign-receipt-loading">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <p className="text-sm text-slate-500">Preparando recibo…</p>
          </div>
        )}

        {step === STEP_CHANNEL && (
          <div className="space-y-4" data-testid="sign-receipt-channel">
            {previewUrl && (
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                <iframe
                  src={previewUrl}
                  title="Pré-visualização do recibo"
                  className="w-full h-[420px]"
                />
              </div>
            )}
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-900">
              Ao assinar, você declara ter recebido e lido o documento acima.
              Escolha como deseja receber o código de confirmação.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col gap-1 border-slate-300 hover:border-emerald-400 hover:bg-emerald-50"
                disabled={sending}
                onClick={() => sendOtp("sms")}
                data-testid="sign-receipt-channel-sms"
              >
                {sending && channel === "sms" ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Smartphone className="w-6 h-6 text-emerald-600" />
                )}
                <span className="text-sm font-semibold">SMS</span>
                <span className="text-[11px] text-slate-500">
                  Código no seu celular
                </span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col gap-1 border-slate-300 hover:border-emerald-400 hover:bg-emerald-50"
                disabled={sending}
                onClick={() => sendOtp("email")}
                data-testid="sign-receipt-channel-email"
              >
                {sending && channel === "email" ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Mail className="w-6 h-6 text-emerald-600" />
                )}
                <span className="text-sm font-semibold">E-mail</span>
                <span className="text-[11px] text-slate-500">
                  Código no seu e-mail
                </span>
              </Button>
            </div>
          </div>
        )}

        {step === STEP_OTP && (
          <div className="space-y-4" data-testid="sign-receipt-otp">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-xs text-emerald-900">
                Digite abaixo o código de 6 dígitos enviado
                {channel === "sms" ? " por SMS " : " por e-mail "}
                para <strong>{toMasked}</strong>.
              </p>
            </div>
            <Input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="text-center text-2xl tracking-[0.6em] font-mono h-14"
              maxLength={6}
              inputMode="numeric"
              autoFocus
              data-testid="sign-receipt-otp-input"
            />
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => { setOtpCode(""); setStep(STEP_CHANNEL); }}
                className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                data-testid="sign-receipt-back-btn"
              >
                <ArrowLeft className="w-3 h-3" /> Trocar canal
              </button>
              <button
                type="button"
                onClick={() => sendOtp(channel)}
                disabled={sending}
                className="text-emerald-700 hover:text-emerald-800 font-medium"
                data-testid="sign-receipt-resend-btn"
              >
                {sending ? "Reenviando…" : "Reenviar código"}
              </button>
            </div>
          </div>
        )}

        {step === STEP_DONE && (
          <div className="flex flex-col items-center justify-center py-8 gap-3"
               data-testid="sign-receipt-done">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <p className="text-lg font-semibold text-slate-800">
              Recibo assinado!
            </p>
            <div className="w-full max-w-md p-3 bg-sky-50 border border-sky-200 rounded-lg text-center">
              <p className="text-sm text-sky-900 font-semibold uppercase tracking-wide">
                O documento
              </p>
              <p className="text-sm text-sky-900 font-bold mt-1">
                &ldquo;{documentTitle}&rdquo;
              </p>
              <p className="text-sm text-sky-900 font-semibold uppercase tracking-wide mt-1">
                estará disponível para a leitura
                <br />no seu painel do funcionário.
              </p>
            </div>
            <p className="text-xs text-slate-500 text-center">
              O termo de aceite foi registrado com data, hora, IP e hash.
              Você pode consultá-lo em <strong>Meus Documentos</strong> a
              qualquer momento.
            </p>
            {signatureId && (
              <p className="text-[11px] text-slate-400 font-mono">
                ID {signatureId}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === STEP_OTP && (
            <Button
              onClick={confirmOtp}
              disabled={signing || otpCode.length < 6}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sign-receipt-confirm-btn"
            >
              {signing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Assinando…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Assinar recibo</>
              )}
            </Button>
          )}
          {step === STEP_DONE && (
            <>
              <Button
                variant="outline"
                onClick={downloadSigned}
                data-testid="sign-receipt-download-btn"
              >
                Baixar recibo assinado
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="sign-receipt-close-btn"
              >
                Fechar
              </Button>
            </>
          )}
          {(step === STEP_CHANNEL || step === STEP_PREPARE) && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="sign-receipt-cancel-btn"
            >
              Cancelar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
